import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck, ArrowLeft, UserRound, CheckCircle2, Landmark } from "lucide-react";
import { WillComparison } from "@/components/WillComparison";

export default function Pay() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]);
  const [method, setMethod] = useState("online");
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const BANK = {
    name: "Rehoboth Wealth Group Ltd", bank: "HSBC Bank",
    sortCode: "40-11-56", account: "31139258", reference: "Your full name",
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/payment/items");
      setItems(data.items);
      setSelected(data.items.filter((i) => i.payment_status !== "paid").map((i) => i.key));
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  const toggle = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  const total = (items || []).filter((i) => selected.includes(i.key) && i.payment_status !== "paid").reduce((sum, i) => sum + i.amount, 0);

  const pay = async () => {
    const hasCustom = method === "online" && parseFloat(customAmount) > 0;
    if (selected.length === 0 && !hasCustom) { toast.error("Select an item or enter an amount to pay"); return; }
    setLoading(true);
    try {
      const body = { item_keys: selected, method, origin_url: window.location.origin };
      if (hasCustom) body.custom_amount = parseFloat(customAmount);
      const { data } = await api.post("/payment/checkout", body);
      if (data.method === "adviser") {
        toast.success("Noted — you'll pay your adviser directly. You can still submit your forms.");
        navigate("/dashboard");
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const chargeAmount = method === "online" && parseFloat(customAmount) > 0 ? parseFloat(customAmount) : total;

  if (!items)
    return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><Loader2 className="w-8 h-8 animate-spin text-brand-green" /></div>;

  return (
    <div className="min-h-screen bg-brand-bg">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <button onClick={() => navigate("/dashboard")} className="text-sm text-brand-muted hover:text-brand-green flex items-center gap-1 mb-6" data-testid="pay-back">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>
        <h1 className="font-serif text-3xl md:text-4xl text-brand-green">Payment</h1>
        <p className="text-brand-muted mt-1">Select the services you'd like to pay for, then choose how to pay. You can also submit your forms without paying online.</p>

        <div className="mt-8"><WillComparison /></div>

        <div className="max-w-2xl mx-auto mt-10">
        {items.length === 0 ? (
          <div className="mt-6 rounded-xl border border-brand-line bg-white p-6 text-brand-muted text-sm" data-testid="pay-empty">
            No payable services yet. Complete a Will, LPA or Legacy Trust instruction first, then return here.
          </div>
        ) : (
          <>
            <div className="mt-6 space-y-3">
              {items.map((i) => {
                const paid = i.payment_status === "paid";
                const adviser = i.payment_status === "pay_at_adviser";
                return (
                  <div key={i.key} className={`rounded-xl border p-4 flex items-center justify-between gap-3 ${paid ? "border-brand-green/40 bg-brand-admin" : "border-brand-line bg-white"}`} data-testid={`pay-item-${i.key}`}>
                    <div className="flex items-center gap-3">
                      {!paid && <Checkbox checked={selected.includes(i.key)} onCheckedChange={() => toggle(i.key)} data-testid={`pay-select-${i.key}`} />}
                      <div>
                        <div className="font-medium text-brand-ink text-sm">{i.label}</div>
                        <div className="text-xs text-brand-muted">£{i.ex_vat.toFixed(2)} + VAT
                          {paid && <span className="text-brand-success font-semibold ml-2">Paid</span>}
                          {adviser && <span className="text-brand-gold font-semibold ml-2">Paying adviser directly</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-lg font-semibold text-brand-ink">£{i.amount.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <button onClick={() => setMethod("online")} data-testid="pay-method-online"
                className={`text-left rounded-xl border p-4 transition-all ${method === "online" ? "border-brand-green ring-2 ring-brand-green/30 bg-white" : "border-brand-line bg-white hover:shadow-md"}`}>
                <div className="flex items-center gap-2 font-semibold text-brand-green"><Lock className="w-4 h-4" /> Pay online now</div>
                <p className="text-xs text-brand-muted mt-1">Secure card payment via Stripe.</p>
              </button>
              <button onClick={() => setMethod("adviser")} data-testid="pay-method-adviser"
                className={`text-left rounded-xl border p-4 transition-all ${method === "adviser" ? "border-brand-green ring-2 ring-brand-green/30 bg-white" : "border-brand-line bg-white hover:shadow-md"}`}>
                <div className="flex items-center gap-2 font-semibold text-brand-green"><UserRound className="w-4 h-4" /> Pay adviser directly</div>
                <p className="text-xs text-brand-muted mt-1">Arrange payment with your adviser (card, bank transfer or cheque).</p>
              </button>
            </div>

            {method === "online" && (
              <div className="mt-4 rounded-xl border border-brand-line bg-white p-4" data-testid="custom-amount-block">
                <label className="text-sm font-semibold text-brand-ink">Custom amount (optional)</label>
                <p className="text-xs text-brand-muted mb-2">Leave blank to pay the total of your selected items, or enter a specific amount to pay online.</p>
                <div className="flex items-center gap-2">
                  <span className="text-brand-muted">£</span>
                  <Input type="number" min="0" step="0.01" value={customAmount} placeholder={total.toFixed(2)}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="bg-[#FCFBFA] border-brand-line focus-visible:ring-brand-green max-w-[200px]" data-testid="custom-amount-input" />
                </div>
              </div>
            )}

            {method === "adviser" && (
              <div className="mt-4 rounded-xl border border-brand-line bg-white p-5" data-testid="bank-details-block">
                <div className="flex items-center gap-2 font-semibold text-brand-green mb-3"><Landmark className="w-4 h-4" /> Bank transfer details</div>
                <dl className="text-sm divide-y divide-brand-line">
                  {[["Account name", BANK.name], ["Bank", BANK.bank], ["Sort code", BANK.sortCode], ["Account number", BANK.account], ["Reference", BANK.reference]].map(([k, v]) => (
                    <div key={k} className="flex justify-between py-2">
                      <dt className="text-brand-muted">{k}</dt>
                      <dd className="font-semibold text-brand-ink" data-testid={`bank-${k.toLowerCase().replace(/\s+/g, '-')}`}>{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-brand-muted mt-3">Please use your full name as the payment reference. Confirming below records that you will pay your adviser directly — you can still submit your forms.</p>
              </div>
            )}

            <div className="mt-6 rounded-xl border border-brand-line bg-brand-admin p-5">
              <div className="flex items-center justify-between text-brand-ink">
                <span className="font-semibold">{method === "online" ? "Amount to pay (inc. VAT)" : "Total selected (inc. VAT)"}</span>
                <span className="text-2xl font-semibold text-brand-green" data-testid="pay-total">£{chargeAmount.toFixed(2)}</span>
              </div>
              <Button onClick={pay} disabled={loading || (selected.length === 0 && !(method === "online" && parseFloat(customAmount) > 0))} data-testid="pay-now-btn"
                className="w-full mt-4 bg-brand-green hover:bg-brand-greenHover text-white h-11">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : method === "online" ? <Lock className="w-4 h-4 mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {method === "online" ? "Pay securely with Stripe" : "Confirm — pay adviser directly"}
              </Button>
              <p className="text-xs text-brand-muted mt-3 flex items-center gap-1.5 justify-center">
                <ShieldCheck className="w-3.5 h-3.5" /> Online payments processed securely by Stripe. Test card 4242 4242 4242 4242.
              </p>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
